import React from 'react'
import type { HeaderGroup } from 'react-table'
import cx from 'clsx'

import SortableHeader from '../SortableHeader'
import type { FilterOption, DateFilterValue } from '../FilterableColumnHeader'
import FilterableColumnHeader from '../FilterableColumnHeader'

import { SortOrder } from '@type/common'

export interface ColumnFilterConfig {
  filterKey: string
  filterOptions: FilterOption[]
  selectedFilters: string[]
  onFilterChange: (values: string[]) => void
  isDateColumn?: boolean
  dateValue?: DateFilterValue
  onDateChange?: (value: DateFilterValue | null) => void
  isTextSearch?: boolean
  textValue?: string
  onTextChange?: (value: string) => void
  asyncSearchFn?: (query: string) => Promise<FilterOption[]>
  onAsyncFilterChange?: (options: FilterOption[]) => void
  isNumericFilter?: boolean
}

export interface TableHeaderProps<TRecord extends object> {
  headerGroup: HeaderGroup<TRecord>
  className?: string
  thClassName?: string
  thClassNames?: { [key: string]: string }
  isDnd?: boolean
  columnFilterConfigs?: Record<string, ColumnFilterConfig>
  sortState?: Record<string, { order: SortOrder; index: number }>
}

export const TableHeader = <TRecord extends object>({
  headerGroup,
  className,
  thClassName,
  thClassNames,
  isDnd = false,
  columnFilterConfigs = {},
  sortState = {},
}: TableHeaderProps<TRecord>) => {
  const { key, ...props } = headerGroup.getHeaderGroupProps()
  const dndCmp = isDnd && <th className={cx(thClassName)} />

  return (
    <tr key={key} {...props} className={cx(className)}>
      {dndCmp}
      {headerGroup.headers.map((column) => {
        // See the matching Row.tsx comment — ``width`` propagates so columns
        // can pin themselves and avoid table-auto reflow between fetches.
        const { key, ...props } = column.getHeaderProps({
          style: { maxWidth: column.maxWidth, width: column.width },
        })
        const sortProps = column.getSortByToggleProps()
        const filterConfig = columnFilterConfigs[column.id]
        const sortInfo = sortState[column.id]
        const currentOrder =
          sortInfo?.order ?? (column.isSorted ? (column.isSortedDesc ? SortOrder.desc : SortOrder.asc) : undefined)

        let header: React.ReactNode

        // Honor both react-table's computed ``column.canSort`` (which
        // already reflects the column-level ``disableSortBy: true``
        // standard) AND the codebase's older ``defaultCanSort: false``
        // convention. The select_row column on the team page sets
        // ``disableSortBy`` to keep the sort chevron from rendering on
        // top of a checkbox; other columns rely on defaultCanSort.
        const canSort = column.defaultCanSort !== false && column.canSort

        if (!canSort && !filterConfig) {
          // Non-sortable column without filter
          header = column.render('Header')
        } else if (filterConfig) {
          // Sortable and/or filterable column - use FilterableColumnHeader
          header = (
            <FilterableColumnHeader
              order={currentOrder}
              onSortClick={canSort ? sortProps.onClick : undefined}
              filterOptions={filterConfig.filterOptions}
              selectedFilters={filterConfig.selectedFilters}
              onFilterChange={filterConfig.onFilterChange}
              isDateColumn={filterConfig.isDateColumn}
              dateValue={filterConfig.dateValue}
              onDateChange={filterConfig.onDateChange}
              isTextSearch={filterConfig.isTextSearch}
              textValue={filterConfig.textValue}
              onTextChange={filterConfig.onTextChange}
              asyncSearchFn={filterConfig.asyncSearchFn}
              onAsyncFilterChange={filterConfig.onAsyncFilterChange}
              isNumericFilter={filterConfig.isNumericFilter}
              className={(column as unknown as { className: string }).className}
            >
              {column.render('Header')}
            </FilterableColumnHeader>
          )
        } else {
          // Sortable but not filterable - use original SortableHeader
          header = (
            <SortableHeader
              onClick={sortProps.onClick}
              order={currentOrder}
              className={(column as unknown as { className: string }).className}
            >
              {column.render('Header')}
            </SortableHeader>
          )
        }

        return (
          <th key={key} {...props} className={thClassName}>
            <div className={thClassNames && thClassNames[column.id]}>{header}</div>
          </th>
        )
      })}
    </tr>
  )
}

export default React.memo(TableHeader)
